import { Link } from 'react-router-dom';
import { ArrowRight, Mail } from 'lucide-react';
import { BrandMark } from '../shell/BrandMark.js';
import { Button } from '../components/ui/button';

declare const __APP_VERSION__: string;

const PILLARS = [
  {
    color: 'bg-greeniq-500',
    title: 'Daily AI briefing',
    body: 'Aggregated satellite + weather + sensor data distilled into one P1/P2/P3 list before 6am.',
  },
  {
    color: 'bg-[color:var(--color-info)]',
    title: 'Precision irrigation',
    body: 'Hunter Pilot + OpenET integration turns FAO-56 models into zone-level runtimes.',
  },
  {
    color: 'bg-[color:var(--color-warning)]',
    title: 'Compliance without paperwork',
    body: 'REI signage, FRAC rotations, and state pesticide reports generated from application logs.',
  },
];

export function LandingPage() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-bg-page text-fg">
      {/* Ambient glow orbs — mirrors the signed-in app shell */}
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div
          className="absolute animate-[landingOrb_9s_ease-in-out_infinite_alternate]"
          style={{
            width: 520,
            height: 520,
            top: -160,
            left: -140,
            borderRadius: '50%',
            background: 'var(--green-500)',
            filter: 'blur(140px)',
            opacity: 0.20,
          }}
        />
        <div
          className="absolute animate-[landingOrb_11s_ease-in-out_-4s_infinite_alternate]"
          style={{
            width: 420,
            height: 420,
            bottom: -120,
            right: -120,
            borderRadius: '50%',
            background: '#3daeff',
            filter: 'blur(150px)',
            opacity: 0.12,
          }}
        />
        <div
          className="absolute animate-[landingOrb_13s_ease-in-out_-2s_infinite_alternate]"
          style={{
            width: 340,
            height: 340,
            bottom: '28%',
            right: '20%',
            borderRadius: '50%',
            background: 'var(--green-400)',
            filter: 'blur(120px)',
            opacity: 0.08,
          }}
        />
      </div>

      {/* Topographic grid motif (very subtle) */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg, transparent 0 6px, rgba(34,212,122,0.8) 6px 7px), repeating-linear-gradient(90deg, transparent 0 18px, rgba(34,212,122,0.4) 18px 19px)',
          backgroundSize: '200px 200px',
        }}
      />

      <div className="relative z-10 flex min-h-screen flex-col">
        <header className="flex h-20 items-center justify-between px-6 md:px-12">
          <div className="flex items-center gap-2.5">
            <BrandMark className="h-10 w-10" />
            <span className="font-display text-2xl font-bold tracking-tight">
              Green<span className="text-greeniq-400">IQ</span>
              <span className="text-greeniq-500">.</span>
            </span>
          </div>
          <nav className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm">
              <Link to="/sign-in">Sign in</Link>
            </Button>
            <Button asChild size="sm">
              <Link to="/sign-up">
                Request demo <ArrowRight size={14} />
              </Link>
            </Button>
          </nav>
        </header>

        <section className="mx-auto w-full max-w-5xl flex-1 px-6 pb-24 pt-16 md:px-12">
          {/* Motif bar — replaces the app-hub pink→teal with GreenIQ green→deep */}
          <div className="mb-8 flex items-center gap-3">
            <span className="relative block h-5 w-8 overflow-hidden rounded-sm bg-greeniq-500 shadow-glow-green">
              <span
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    'repeating-linear-gradient(90deg, transparent 0 2px, rgba(0,0,0,0.35) 2px 3px)',
                }}
              />
            </span>
            <span
              aria-hidden
              className="h-px flex-1"
              style={{
                background:
                  'linear-gradient(90deg, rgba(34,212,122,0.45) 0%, transparent 100%)',
              }}
            />
          </div>

          <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-greeniq-400">
            Course Intelligence Platform
          </p>
          <h1 className="mt-4 font-display text-5xl font-bold leading-[1.05] tracking-tight md:text-6xl">
            Intelligence for <em className="not-italic text-greeniq-400">every acre.</em>
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-fg-secondary">
            GreenIQ aggregates satellite imagery, on-course weather, in-ground sensors, irrigation
            telemetry, disease prediction models, and regulatory compliance into a single daily
            briefing for the golf course superintendent.
          </p>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-fg-muted">
            Built by AGEYE Technologies &mdash; the company that deploys precision-ag sensor
            infrastructure at scale.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild size="lg">
              <Link to="/sign-up">
                Request a demo <ArrowRight size={16} />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href="mailto:nick@ageyetech.com">
                <Mail size={16} /> Talk to Nick
              </a>
            </Button>
          </div>

          <ul className="mt-16 grid gap-4 md:grid-cols-3">
            {PILLARS.map((p) => (
              <li
                key={p.title}
                className="rounded-lg border border-border bg-bg-card/60 p-5 backdrop-blur-[2px] transition-colors hover:border-greeniq-500/30 hover:bg-bg-card"
              >
                <div className="flex items-start gap-3">
                  <span className={`mt-1.5 size-1.5 shrink-0 rounded-full ${p.color}`} />
                  <div>
                    <div className="font-display text-sm font-semibold text-fg">{p.title}</div>
                    <p className="mt-1 text-sm leading-relaxed text-fg-secondary">{p.body}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <footer className="border-t border-border/60 px-6 py-6 md:px-12">
          <div className="mx-auto flex w-full max-w-5xl items-center justify-between text-xs text-fg-muted">
            <span>© {new Date().getFullYear()} AGEYE Technologies</span>
            <span className="font-mono tracking-wider" title={`Build ${__APP_VERSION__}`}>
              v{__APP_VERSION__} · greeniq.ageyetech.com
            </span>
          </div>
        </footer>
      </div>

      <style>{`
        @keyframes landingOrb {
          from { transform: scale(1); opacity: var(--orb-from, 0.18); }
          to { transform: scale(1.15); opacity: var(--orb-to, 0.28); }
        }
      `}</style>
    </div>
  );
}
